
// Pure helpers for a LineString feature's "parametric arc" metadata (see
// arcFromSagitta.js for the arc math itself). No DOM/THREE/FeatureManager
// dependency - operate on a plain `feature` object (or plain coordinate
// arrays for reconcileArcRanges()) and return plain data; the caller (see
// DigitizationModule.js/VectorEditingMenu.js) is responsible for actually
// persisting the result via featureManager.setDomainData(feature.id, "arc",
// {ranges}).
//
//   Arc-range store (pure)
//           |
//           +-- getArcRanges()            - read
//           +-- findArcRangeAt()           - read, by vertex index
//           +-- findArcRangeForSegment()    - read, by segment (edge) index
//           +-- upsertArcRange()           - write, one range replaced/added
//           +-- removeArcRange()           - write, one range removed ("Opløs bue")
//           +-- resampleArcRange()         - write, ONE range's own vertex-count change (dynamic sampling)
//           +-- reconcileArcRanges()       - write, invalidation after ANY OTHER geometry edit
//
// ── Storage location ─────────────────────────────────────────────────────
// A parametric arc is deliberately NOT a new GIS feature type (task §6: "det
// underliggende feature forbliver en almindelig LineString") - it is a
// namespaced annotation on an ordinary LineString feature, using the SAME
// `feature.domainData` extensibility point the DANVA domain layer already
// uses (see FeatureManager.setDomainData()/Feature.js), under its own
// "arc" namespace so it never collides with "danva" or any other domain:
//
//   feature.domainData.arc = {
//     ranges: [{startIndex, endIndex, sagittaM, planeU: [x, y, z]}, ...]
//   }
//
// Deliberately minimal: only the SIGNED sagitta and the arc plane's `u`
// (in-plane, perpendicular-to-chord) unit vector are stored. The chord
// direction `d` and both endpoints are NOT stored - they are always safely
// re-derivable live from points[startIndex]/points[endIndex], because ANY
// move of either endpoint already invalidates the range (see
// reconcileArcRanges() below) before those stale values could ever be read.
// Ranges never overlap (each vertex belongs to at most one parametric arc).

function rangesOf(feature){
	if(!feature || !feature.domainData || !feature.domainData.arc || !feature.domainData.arc.ranges) return [];
	return feature.domainData.arc.ranges;
}

function overlaps(a, startIndex, endIndex){
	return a.startIndex <= endIndex && startIndex <= a.endIndex;
}

export function getArcRanges(feature){
	return rangesOf(feature);
}

// The stored range whose [startIndex..endIndex] contains this single vertex
// index, or null.
export function findArcRangeAt(feature, vertexIndex){
	const found = rangesOf(feature).find((r) => r.startIndex <= vertexIndex && vertexIndex <= r.endIndex);
	return found === undefined ? null : found;
}

// The stored range that fully contains the segment (edge) running from
// vertex `segmentStartIndex` to `segmentStartIndex + 1`, or null. A segment
// belongs to an arc only when BOTH its vertices are within the same stored
// range (a segment straddling an arc's boundary is ordinary geometry).
export function findArcRangeForSegment(feature, segmentStartIndex){
	const segEnd = segmentStartIndex + 1;
	const found = rangesOf(feature).find((r) => r.startIndex <= segmentStartIndex && segEnd <= r.endIndex);
	return found === undefined ? null : found;
}

// Returns a NEW ranges array with `range` added, replacing any existing
// range(s) it overlaps (arcs never overlap - a fresh definition for a span
// always wins over whatever was there before). Used both for a brand-new
// "Bue fra pilhøjde" and for the live-preview/"Rediger bue"/"Vend bue" dialog
// re-asserting its own range's fresh metadata after each of its own edits.
export function upsertArcRange(feature, range){
	const kept = rangesOf(feature).filter((r) => !overlaps(r, range.startIndex, range.endIndex));
	return [...kept, range].sort((a, b) => a.startIndex - b.startIndex);
}

// Returns a NEW ranges array with the range at exactly [startIndex,endIndex]
// removed ("Opløs bue" - task §8: coordinates are left exactly as they are,
// only the parametric metadata is dropped). A no-op (returns an equal-value
// array) if no such range exists.
export function removeArcRange(feature, startIndex, endIndex){
	return rangesOf(feature).filter((r) => !(r.startIndex === startIndex && r.endIndex === endIndex));
}

// The arc tool's OWN dynamic resampling (task §2-§4: vertex count adapts
// live to the arc's geometric size/tolerance) changes the edited range's own
// vertex count on almost every live-preview step - the generic
// reconcileArcRanges() rule below would otherwise unconditionally clear
// EVERY stored range the instant that happens (it can't tell "the arc tool
// resampled itself" apart from "the user inserted/deleted a vertex
// elsewhere"). This function is the arc tool's own, PRECISE alternative to
// that generic rule for exactly this one case: given the ranges array AS IT
// WAS immediately before the resampling geometry change (`ranges` - the
// caller MUST snapshot this before calling visual.setPoints(), since that
// call synchronously triggers the generic rule and would otherwise already
// have wiped everything by the time this runs), the edited range's OLD
// bounds (`oldRange` - matches the caller's own bookkeeping of what it
// asserted last, or the pre-existing stored range when first opening
// "Rediger bue…"), and its fresh record after resampling (`newRange`):
//
//   - the entry matching `oldRange`'s exact bounds is replaced by `newRange`
//     (its startIndex is always unchanged - only an interior/endIndex
//     resample ever happens; the chord's own start vertex index is fixed);
//   - every OTHER (sibling) range is left untouched if it lies entirely
//     BEFORE the edited range, or shifted by the exact same index delta the
//     resampling introduced if it lies entirely AFTER it - ranges never
//     overlap (see this file's header comment), so "before" or "after" is
//     always unambiguous, never partial.
//
// Ordinary manual insert/delete elsewhere on the feature is NOT affected by
// this function at all - it keeps going through the generic, deliberately
// conservative reconcileArcRanges() rule untouched (task §4: "den
// eksisterende konservative opløsningsregel [må] gerne bevares" for that
// case).
export function resampleArcRange(ranges, oldRange, newRange){
	const oldSpan = oldRange.endIndex - oldRange.startIndex;
	const newSpan = newRange.endIndex - newRange.startIndex;
	const delta = newSpan - oldSpan;

	const kept = ranges
		.filter((r) => !(r.startIndex === oldRange.startIndex && r.endIndex === oldRange.endIndex))
		.map((r) => (r.startIndex > oldRange.endIndex
			? {...r, startIndex: r.startIndex + delta, endIndex: r.endIndex + delta}
			: r));

	return [...kept, newRange].sort((a, b) => a.startIndex - b.startIndex);
}

const COORD_EPS = 1e-9;

function samePosition(a, b){
	return Math.abs(a.x - b.x) < COORD_EPS && Math.abs(a.y - b.y) < COORD_EPS && Math.abs(a.z - b.z) < COORD_EPS;
}

// Invalidation rule run after EVERY geometry edit to a LineString feature
// (task §9: "hvis brugeren... bryder buedefinitionen, skal buen opløses...
// undgå skjulte tvangsregler"), unconditional and deliberately simple:
//
//   - if the vertex COUNT changed anywhere in the feature (insert/delete),
//     every stored range's index space is no longer trustworthy - ALL
//     ranges are dropped. A conservative, documented limitation (task §9
//     explicitly allows this: "hold den første implementering simpel og
//     robust") rather than attempting to re-map indices across an
//     insert/delete.
//   - otherwise, a range survives only if EVERY vertex position within its
//     [startIndex..endIndex] is unchanged (beyond floating-point noise) -
//     moving/snapping ANY vertex belonging to an arc (including an
//     endpoint) dissolves THAT range; other ranges on the same feature are
//     unaffected.
//
// `oldCoords`/`newCoords` are plain [[x,y,z],...] coordinate arrays (the
// same shape as feature.geometry.coordinates / FeatureVisual.rawCoordinates()
// for a LineString) - NOT full {x,y,z} point objects, so this can be called
// directly from DigitizationModule's geometry_changed listener with the
// feature's own pre/post coordinate arrays.
//
// The one deliberate exception: the arc-editing dialog itself calls
// upsertArcRange() again immediately after every one of ITS OWN setPoints()
// calls (live preview and commit alike), re-asserting the very range this
// function would otherwise just have dropped - so the dialog never needs a
// suppression flag, and this function stays a single, unconditional rule.
export function reconcileArcRanges(feature, oldCoords, newCoords){
	const ranges = rangesOf(feature);
	if(ranges.length === 0) return ranges;

	if(oldCoords.length !== newCoords.length) return [];

	return ranges.filter((r) => {
		for(let i = r.startIndex; i <= r.endIndex; i++){
			const o = oldCoords[i], n = newCoords[i];
			if(!o || !n) return false;
			if(!samePosition({x: o[0], y: o[1], z: o[2]}, {x: n[0], y: n[1], z: n[2]})) return false;
		}
		return true;
	});
}
